import React, { useCallback, useEffect, useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';
import cx from 'classnames';
import { Eye, EyeSlash } from 'phosphor-react';
import { isNil } from 'ramda';
import { Field, Form, useForm, useFormState } from 'react-final-form';
import { useDispatch, useSelector } from 'react-redux';
import Select, { components } from 'react-select';

import FormSection from 'components/Layouts/Form/FormSection';
import LayoutWithFooter from 'components/Layouts/shared/LayoutWithFooter';
import BaseMask from 'components/shared/FormAdapters/FormBase/Mask';
import FormGroupAdapter from 'components/shared/FormAdapters/FormDefault/FormGroup';
import { DropdownIndicator } from 'components/shared/FormAdapters/FormDefault/ReactSelect';
import selectStyles from 'components/shared/FormAdapters/FormDefault/ReactSelect/index.module.css';
import FormErrorText from 'components/shared/FormErrorText';
import Icon from 'components/shared/Icon';
import Check2 from 'components/shared/Icon/icons/Check2';
import Trash from 'components/shared/Icon/icons/Trash';
import MenuActionsHelper from 'components/shared/MenuActionsHelper';
import Modal from 'components/shared/Modal';
import Text from 'components/shared/Typography/Text';
import UnsavedChangesFormWatcher from 'components/shared/UnsavedChangesFormWatcher';
import { useFormFieldPopup } from 'hooks/useFormFieldPopup';
import { useScrollPageToTop } from 'hooks/useScrollPageToTop';
import { itinerariesItemDataSelector } from 'store/itinerariesItem/selectors';
import { closeModalAction, openModalAction } from 'store/modal';
import { isModalOpenedSelector, modalDataSelector } from 'store/modal/selectors';
import { setAppHasChangesAction } from 'store/unsavedChanges';
import axiosApi from 'utils/axios';
import { reverseFlightDateValues } from 'utils/common';
import {
  useItinerariesItemNextStepFormHandler,
  useItinerariesItemSaveHandler,
} from 'utils/itineraries/functions/builderUtils';
import { calcNumberOfNightsOfSchedule } from 'utils/itineraryDetails';
import { doesOptionContainPart, searchInOptions } from 'utils/search';

import ItinerariesItemActionButtons from '../../ItinerariesItemBuilder/ActionButtons';
import ItinerariesItemNextButton from '../../ItinerariesItemBuilder/ItinerariesItemNextButton';
import { BuilderFooterButton } from '../../PageTypes/shared/BuilderFooterButton';
import {
  CURRENCY_TYPES,
  DAY_COUNTING_STRATEGIES,
  NUMBER_OPTIONS,
  PAYMENT_STRATEGIES,
  PAYMENT_STRATEGY_OPTIONS,
  ROOMING_TYPE_OPTIONS,
  ROOMING_TYPES,
} from './constants';
import { useCosts, useCostsTemplates } from './functions';
import styles from './index.module.css';
import SaveTemplateModal from './SaveTemplateModal';

const cleanupMask = (v) => (v ? reverseFormatCurrency(v) : undefined);
const MAX_CURRENCY_VALUE = 9999999999.99;
const reverseFormatCurrency = (currency) => {
  if (!currency) {
    return undefined;
  }

  const stripped = currency.replace(/(,|\$| )/g, '');
  const converted = Number(stripped);

  if (Number.isNaN(converted)) {
    throw new Error(`convertFormattedCurrency > invalid value supplied "${currency}"`);
  }

  const normalized = converted > MAX_CURRENCY_VALUE ? MAX_CURRENCY_VALUE : converted;

  return normalized;
};

function CurrencyInput({ input, className }) {
  return (
    <BaseMask
      typeOfMask="currency"
      className={cx(styles.currencyValueInput, className)}
      onChange={input.onChange}
      value={input.value}
      inputMode="numeric"
    />
  );
}

const transformValuesToBEForm = (itinerary, values) => {
  return {
    ...itinerary,
    ...(itinerary?.tripEvents && {
      tripEvents: itinerary.tripEvents.map((t) => {
        if (t.data?.isIDL) {
          return reverseFlightDateValues(t);
        }
        return t;
      }),
    }),
    costs: values,
    costsAccept: values.accept,
  };
};

export function Costs() {
  useScrollPageToTop();
  const { validate } = useCosts();
  const dispatch = useDispatch();
  const handleNextStep = useItinerariesItemNextStepFormHandler();
  const handleSave = useItinerariesItemSaveHandler();
  const [isSaving, setIsSaving] = React.useState(false);

  const openModal = (templateData) => {
    dispatch(openModalAction({ type: 'SAVE_TEMPLATE', data: { templateData } }));
  };

  const itinerary = useSelector(itinerariesItemDataSelector);
  const predefinedDaysCount = useMemo(() => {
    const num = calcNumberOfNightsOfSchedule(itinerary.scheduleType, itinerary.schedules);
    return num > 100 ? 100 : num;
  }, [itinerary]);
  const shouldPredefineDaysCount = isNil(itinerary.costs?.daysCount) && predefinedDaysCount > 0;
  const initialDataMemoized = useMemo(
    () => ({
      type: PAYMENT_STRATEGIES.PERSON,
      guestCount: 2,
      daysCount: shouldPredefineDaysCount ? predefinedDaysCount : 10,
      daysType: DAY_COUNTING_STRATEGIES.NIGHTS,
      currency: CURRENCY_TYPES.USD,
      accommodationType: ROOMING_TYPES.SHARED,
      roomingConfigurationEnabled: true,
      notesEnabled: true,
      topTextEnabled: true,
      accept: itinerary.costsAccept,
      ...itinerary.costs,
    }),
    [itinerary],
  );

  return (
    <LayoutWithFooter>
      {({ footerComponent: FooterComponent }) => (
        <>
          <Form
            validate={validate}
            onSubmit={(values) => handleNextStep(transformValuesToBEForm(itinerary, values))}
            initialValues={initialDataMemoized}
          >
            {({ handleSubmit, submitError, submitting, hasValidationErrors, values }) => {
              // eslint-disable-next-line react-hooks/rules-of-hooks
              useEffect(() => {
                if (!values.accept) {
                  handleSubmit();
                }
              }, []);

              return (
                <form onSubmit={handleSubmit} className={styles.form}>
                  <UnsavedChangesFormWatcher />

                  <FormSection type="rounded">
                    <TemplateSection />
                  </FormSection>

                  <FormSection type="rounded">
                    <PriceSection />
                  </FormSection>

                  <FormSection type="rounded">
                    <MiscellaneousSection />
                  </FormSection>

                  <FormSection type="rounded">
                    <NoteSection />
                  </FormSection>

                  <FormSection type="rounded">
                    <IncludeSection />
                  </FormSection>

                  {submitError && <FormErrorText>{submitError}</FormErrorText>}

                  <FooterComponent>
                    <ItinerariesItemNextButton
                      id="itineraryCostsNextButton"
                      isSaving={isSaving}
                      leftChildren={
                        <BuilderFooterButton type="default" onClick={() => openModal(values)} disabled={isSaving}>
                          Save as Template
                        </BuilderFooterButton>
                      }
                    />
                  </FooterComponent>

                  <ItinerariesItemActionButtons
                    hasValidationErrors={hasValidationErrors}
                    submitting={submitting}
                    onSave={() => handleSave(transformValuesToBEForm(itinerary, values))}
                    onIsSavingChange={setIsSaving}
                  />
                </form>
              );
            }}
          </Form>
        </>
      )}
    </LayoutWithFooter>
  );
}

const TemplateSection = () => {
  const { templateOptions, createTemplate, updateTemplate, updatableTemplateOptions, setTemplate, deleteTemplate } =
    useCostsTemplates();
  const form = useForm();
  const isSaveTemplateModalOpen = useSelector(isModalOpenedSelector('SAVE_TEMPLATE'));
  const modalData = useSelector(modalDataSelector);
  const dispatch = useDispatch();
  const closeModal = () => {
    dispatch(closeModalAction());
  };

  const OptionMemoized = useCallback(
    (props) => (
      <div className={styles.option}>
        <components.Option {...props}>
          {props.children}

          {props.data.policies?.destroy && (
            <Icon
              component={Trash}
              width="14px"
              height="14px"
              fill="none"
              className={styles.optionDeleteButton}
              onClick={(event) => {
                event.stopPropagation();
                deleteTemplate(props.data.value);
              }}
            />
          )}
        </components.Option>
      </div>
    ),
    [deleteTemplate],
  );

  const Menu = useCallback((props) => {
    return (
      <components.Menu {...props}>
        {props.children}
        <MenuActionsHelper center />
      </components.Menu>
    );
  });

  return (
    <>
      <Text type="brandon" isStrong weight="500">
        Start From Template
      </Text>

      <div className={cx(selectStyles.reactSelectContainer, styles.selectTemplateContainer)}>
        <div className={styles.selectTemplateInputWrap}>
          <Select
            options={templateOptions}
            name="template"
            onChange={(template) => {
              setTemplate(template.value, form);
            }}
            components={{ Option: OptionMemoized, Menu, DropdownIndicator }}
            placeholder="Select Template"
            classNamePrefix="react-select"
            noOptionsMessage={() => 'No templates available yet'}
          />
        </div>
      </div>

      <Modal size="md" isOpen={isSaveTemplateModalOpen} toClose={closeModal}>
        {isSaveTemplateModalOpen && (
          <SaveTemplateModal
            onCreate={createTemplate}
            onUpdate={updateTemplate}
            templateData={modalData.templateData}
            close={closeModal}
            options={updatableTemplateOptions}
          />
        )}
      </Modal>
    </>
  );
};

const PriceSection = () => {
  const dispatch = useDispatch();
  const form = useForm();
  const { values } = useFormState();

  const { data: currencies } = useQuery(
    ['refs', 'currencies'],
    () =>
      axiosApi
        .get('/refs/currencies')
        .then((response) =>
          Object.entries(response.data).map(([value, currency]) => ({
            label: currency.name,
            value,
            currency,
          })),
        )
        .catch((e) => {
          throw new Error(e.errors.status);
        }),
    { staleTime: Infinity },
  );

  return (
    <>
      <div className={styles.sectionHeader}>
        <Text type="brandon" isStrong weight="600" className={styles.sectionLabel}>
          Price
        </Text>

        <div className={styles.inlineSelectContainerRight}>
          {!values['topTextEnabled'] && (
            <div className={styles.hiddenLabel}>
              <Text type="brandon" size="medium" weight="500">
                Section is hidden
              </Text>
            </div>
          )}

          <button
            type="button"
            className={styles.hideableSectionToggler}
            onClick={() => {
              form.change('topTextEnabled', !values['topTextEnabled']);
              dispatch(setAppHasChangesAction(true));
            }}
          >
            {values['topTextEnabled'] ? 'Hide Section' : 'Show Section'}

            <Icon component={values['topTextEnabled'] ? EyeSlash : Eye} width="2.2rem" height="2.2rem" fill="none" />
          </button>
        </div>
      </div>

      <div>
        <div className={!values['topTextEnabled'] ? styles.disabledBlock : undefined}>
          <div className={styles.inlineSelectContainer}>
            Total
            <Field
              component={FormGroupAdapter}
              name="type"
              type="inlineSelect"
              options={PAYMENT_STRATEGY_OPTIONS}
              noMargin
              inline
              lowercaseLabel
              className={styles.priceSelect}
            />
            based on
            <Field
              component={FormGroupAdapter}
              name="guestCount"
              type="inlineSelect"
              options={NUMBER_OPTIONS}
              noMargin
              inline
              lowercaseLabel
              renderLabel={(option) => `(${option?.label || '?'})`}
              minWidth={50}
              className={styles.priceSelect}
            />
            guests in
            <Field
              component={FormGroupAdapter}
              name="accommodationType"
              type="inlineSelect"
              options={ROOMING_TYPE_OPTIONS}
              noMargin
              inline
              lowercaseLabel
              className={styles.priceSelect}
            />
            accommodation
            <div className={styles.currency}>
              {!values.determined && (
                <>
                  <Field
                    component={FormGroupAdapter}
                    name="currency"
                    type="inlineSelect"
                    options={currencies || []}
                    noMargin
                    inline
                    searchable
                    groups={[
                      { label: 'Popular Currencies', value: 'popular' },
                      { label: 'All Currencies', value: 'all' },
                    ]}
                    groupBy={(option) => (option.currency.priority < 50 ? 'popular' : 'all')}
                    searchFn={(o, s) => searchInOptions(o, s, { filterFn: (o) => doesOptionContainPart(o, s) })}
                    renderOption={(opt, { selectedOption, group, search }) => {
                      return opt ? (
                        <div
                          className={styles.currencyOption}
                          title={`${opt.currency.disambiguateSymbol || opt.currency.symbol} ${opt.label}`}
                        >
                          <div className={styles.currencyAbbr}>{opt.value}</div>
                          <div className={styles.currencyLabel}>
                            {opt.label.toLowerCase().includes(search) ? (
                              <>
                                {opt.label.slice(0, opt.label.toLowerCase().indexOf(search))}
                                <span className={styles.currencyHighlightedLetter}>
                                  {opt.label.slice(
                                    opt.label.toLowerCase().indexOf(search),
                                    opt.label.toLowerCase().indexOf(search) + search.length,
                                  )}
                                </span>
                                {opt.label.slice(opt.label.toLowerCase().indexOf(search) + search.length)}
                              </>
                            ) : (
                              opt.label
                            )}
                            {group?.value === 'popular'
                              ? ` ${opt.currency.disambiguateSymbol || opt.currency.symbol}`
                              : null}
                          </div>
                          {opt.value === selectedOption?.value && <Check2 className={styles.currencyCheck} />}
                        </div>
                      ) : null;
                    }}
                    renderLabel={(option) =>
                      option ? option.currency.disambiguateSymbol || option.currency.symbol : ''
                    }
                    className={styles.priceCurrencySelect}
                  />{' '}
                  <Field
                    name="value"
                    component={CurrencyInput}
                    parse={cleanupMask}
                    className={styles.priceCurrencyInput}
                  />
                </>
              )}
              {values.determined && <span className={styles.tbd}>TBD</span>}
            </div>
          </div>

          <Field
            component={FormGroupAdapter}
            name="determined"
            type="checkbox"
            labelText="Costs to be Determined"
            checkboxClassName={styles.checkBox}
          />
        </div>

        <hr className={styles.divider} />

        <Field
          component={FormGroupAdapter}
          name="accept"
          type="checkbox"
          labelText="I understand I am responsible for the accuracy of cost inclusions and exclusions, and have made clear that some activities mentioned within the itinerary may incur additional costs unless otherwise explicitly noted."
          checkboxClassName={styles.checkBox}
          className={styles.lastGroup}
        />
      </div>
    </>
  );
};

const MiscellaneousSection = () => {
  return (
    <div className={styles.sectionInner}>
      <Text type="brandon" isStrong weight="600" className={styles.sectionLabel}>
        Miscellaneous
      </Text>
      <HideableSection
        flagName="roomingConfigurationEnabled"
        inputName="roomingConfigurationWysiwyg"
        inputPlaceholder="Enter text..."
        tooltip={
          <Text accent style={{ fontSize: '1.5rem' }}>
            <b>Tool Tip:</b>
            <br />
            <br />
            Add rooming configuration details, quote in different currencies or using different phrasing. Use the text
            toolbar to format the text however you like.
          </Text>
        }
      />
    </div>
  );
};

const NoteSection = () => {
  return (
    <div className={styles.sectionInner}>
      <Text type="brandon" isStrong weight="600" className={styles.sectionLabel}>
        Notes
      </Text>

      <HideableSection
        flagName="notesEnabled"
        inputName="notesWysiwyg"
        inputPlaceholder="Enter notes..."
        tooltip={
          <Text accent style={{ fontSize: '1.5rem' }}>
            <b>Examples:</b>
            <br />
            <br />
            Single supplements
            <br />
            <br />
            Child discounts
            <br />
            <br />
            Costs for another family
            <br />
            <br />
            Costs if other guests join
            <br />
            <br />
            Flights
            <br />
            <br />
            Permits
            <br />
            <br />
            Anything else quoted separately
          </Text>
        }
      />
    </div>
  );
};

const IncludeSection = () => {
  return (
    <>
      <Text type="brandon" isStrong weight="600" className={styles.sectionLabel}>
        Includes and Excludes
      </Text>

      <Text type="brandon" className={styles.includesLabel}>
        This cost includes:
      </Text>
      <Field
        component={FormGroupAdapter}
        name="includesWysiwyg"
        placeholder="Enter text..."
        type="editor"
        className={styles.textarea}
      />

      <Text type="brandon" className={styles.includesLabel}>
        This cost does not include:
      </Text>
      <Field
        component={FormGroupAdapter}
        name="notIncludesWysiwyg"
        placeholder="Enter text..."
        type="editor"
        className={cx(styles.textarea, styles.lastGroup)}
      />
    </>
  );
};

function HideableSection({ flagName, inputName, inputPlaceholder, tooltip }) {
  const dispatch = useDispatch();
  const form = useForm();
  const { values } = useFormState();
  const showPopup = useFormFieldPopup();

  return (
    <div className={styles.hideableSection}>
      <div className={styles.labelContainer}>
        {!values[flagName] && (
          <div className={styles.hiddenLabel}>
            <Text type="brandon" size="medium" weight="500">
              Section is hidden
            </Text>
          </div>
        )}

        <button
          type="button"
          className={styles.hideableSectionToggler}
          onClick={() => {
            form.change(flagName, !values[flagName]);
            dispatch(setAppHasChangesAction(true));
          }}
        >
          {values[flagName] ? 'Hide Section' : 'Show Section'}
          <Icon component={values[flagName] ? EyeSlash : Eye} width="2.2rem" height="2.2rem" fill="none" />
        </button>
      </div>

      <Field
        component={FormGroupAdapter}
        name={inputName}
        placeholder={inputPlaceholder}
        type="editor"
        disabled={!values[flagName]}
        onFocus={tooltip && showPopup(tooltip)}
        className={cx(styles.textarea, styles.lastGroup)}
      />
    </div>
  );
}
